import moment from "moment-timezone"
import { competitionTile, daysLabel, monthLabel, plural, shortDateLabel } from "./plannerLabels"
import { seasonLabel } from "./plannerQuery"
import { seasonOf } from "./generationRequest"

// The words of „Generarea propunerilor” (wayfinder tickets 09, 11, 17), from what the generation preview answers (Phase D3).

const day = value => moment.utc(value, "YYYY-MM-DD", true)
// "Sat 15 – Sun 16 Nov 2025": a source's days, a year or more before.
export const daysYearLabel = days => `${daysLabel(days)} ${day(days.end).format("YYYY")}`
// "+52 weeks", "+52 weeks, then +1 week", "+104 weeks, then −2 weeks": how the draft's dates were found.
export const weeksLabel = ({ weeks, moveWeeks }) => `+${weeks} weeks${moveWeeks ? `, then ${moveWeeks > 0 ? "+" : "−"}${plural(Math.abs(moveWeeks), "week")}` : ""}`
// An event of the platform by its number ("#38465"), a proposal by its own ("P-1012").
export const refLabel = ref => (!ref ? "" : ref.kind === "proposal" ? ref.number || "a proposal" : ref.eventNumber ? `#${ref.eventNumber}` : "an event")
const named = ref => [refLabel(ref), ref?.name].filter(Boolean).join(" ")
export const sourceLabel = row => named({ kind: "event", ...row.source })
// "Aceline Tennis · Iași", or the club of the source when it has none.
export const draftClubLabel = club => (club ? [club.clubName, club.city].filter(Boolean).join(" · ") || "Club of the platform" : "No club")

// A competition line of a draft (ticket 11): its sizing mark — = kept, ↑ grown, ↓ shrunk, ✕ proposed for removal, + suggested — then its
// level, game type and Main List capacity ("↑ 4S 15").
const LINE_MARKS = { keep: "=", grow: "↑", shrink: "↓", remove: "✕", notFinished: "=", cancelledEdition: "=", suggested: "+" }
export const lineMark = rule => LINE_MARKS[rule] || "="
export const lineLabel = (line, rule = line.source?.rule) => `${lineMark(rule)} ${competitionTile(line)} ${line.mlMaxNumber ?? "—"}`
// The rule of a line and its numbers, as the server words them in the saved proposal's source (D3 `lineSourceDetail`).
const LEVEL_PREFIX = { singles: "S", doubles: "D" }
const areaSeason = ({ season, from }) => (season === "summer" ? `summer ${from.slice(0, 4)}`
  : `winter ${from.slice(0, 4)}/${String(Number(from.slice(0, 4)) + 1).slice(2)}`)
export const lineSourceText = (line, number) => {
  const { source } = line
  const edition = `#${number}`
  const entries = `${source.capacity} places, ${source.mainList} confirmed + ${source.waitingList} on the Waiting List`
  switch (source.rule) {
    case "grow": return `${edition}: ${entries} → next step`
    case "shrink": return `${edition}: ${entries} (under 75 %) → smaller step`
    case "keep": return `${edition}: ${entries} → kept`
    case "remove": return `${edition}: ${source.demand} ${source.demand === 1 ? "entry" : "entries"} (under ${source.minimum}) → proposed for removal`
    case "notFinished": return `${edition} not finished: demand incomplete, ${source.capacity} places kept`
    case "cancelledEdition": return `${edition} was cancelled: ${source.capacity} places, as planned`
    case "suggested": return `${source.competitions} ${LEVEL_PREFIX[line.gameType] || line.gameType}${line.level} competitions within ${source.radiusKm} km, `
      + `${areaSeason(source.season)}, median ${source.median}`
    default: return edition
  }
}
// The number of the edition a line was sized from: the source's, or the last held edition's.
export const editionNumber = (row, eventId = row.draft.previousEdition?.eventId) => (row.lastHeldEdition && eventId === row.lastHeldEdition.eventId
  ? row.lastHeldEdition.eventNumber : row.source.eventNumber)
// "Use the last held edition: #38056 Cupa de primăvară, Sat 7 – Sun 8 Dec 2024" (ticket 17).
export const lastHeldLabel = held => `#${held.eventNumber} ${held.name}, ${daysYearLabel(held)}`

// Why a source cannot become a draft as it is (ticket 17; D3's `datesNotInOrder`): the row stays unticked.
export const REASON_LABELS = {
  clubBlocked: "Club blocked",
  clubDeleted: "Club deleted",
  organizerInactive: "Organizer no longer active",
  organizerNotAtClub: "Organizer no longer at the club",
  datesNotInOrder: "The source event ends before it starts: correct its dates, or make the proposal by hand.",
}
export const reasonLabel = reason => REASON_LABELS[reason] || reason
// A row that can never be saved: its edition ends before it starts (D3, integrator option B).
export const unsaveable = row => row.reasons.includes("datesNotInOrder")
// "Club blocked · Organizer no longer at the club": the facts that untick a source, which "Generate anyway" keeps for the editor.
export const reasonsLine = row => row.reasons.filter(reason => reason !== "datesNotInOrder").map(reasonLabel).join(" · ")
export const CANNOT_BE_SENT = { noOsAccount: "Cannot be sent: no SportyaOS account" }
// "Source: last held edition #38056 (2024); the 2026 edition was cancelled" (ticket 17).
export const lastHeldSource = row => `Source: last held edition #${row.lastHeldEdition.eventNumber} (${row.lastHeldEdition.start.slice(0, 4)}); `
  + `the ${row.source.start.slice(0, 4)} edition was cancelled`
// What the server could not read (D3 `unavailable`): its checks were skipped, so the review shows none of their reasons.
const UNAVAILABLE = {
  clubs: "The club checks were skipped: Clubs could not be read. A blocked or deleted club is not shown here.",
  organizers: "The organizer checks were skipped: Accounts could not be read. An organizer no longer active, no longer at the club or without SportyaOS "
    + "is not shown here.",
  organizerNames: "The organizers' names could not be read.",
}
export const unavailableLines = unavailable => (unavailable || []).map(name => UNAVAILABLE[name] || `${name} could not be read.`)

// A source handled already (ticket 17): continued in the calendar, or answered by a declined or withdrawn proposal.
const ALREADY_PLANNED = "alreadyPlanned"
const HANDLED = ["refused", "withdrawn"]
// "Generate anyway" (ticket 17): a handled source, or one whose club or organizer is no longer usable, made a draft all the same; never a row
// whose edition ends before it starts.
export const canGenerateAnyway = row => !row.ticked && !unsaveable(row) && ([ALREADY_PLANNED, ...HANDLED].includes(row.status) || Boolean(reasonsLine(row)))
// The review's groups (ticket 17, the Generate artboard's notes): last season's sources by the month of their draft, then the declined
// or withdrawn ones, then each older season ("Last edition 2 seasons ago (winter 2024/25)"), unticked, with "Tick the whole group".
export const reviewGroups = rows => {
  const groups = []
  const add = (key, label, row, extra = {}) => {
    let group = groups.find(item => item.key === key)
    if (!group) groups.push((group = { key, label, rows: [], ...extra }))
    group.rows.push(row)
  }
  rows.filter(row => row.seasonsBack === 1 && !HANDLED.includes(row.status)).forEach(row => add("month|" + row.draft.start.slice(0, 7), monthLabel(row.draft.start), row))
  rows.filter(row => HANDLED.includes(row.status)).forEach(row => add("handled", "Declined or withdrawn", row))
  rows.filter(row => row.seasonsBack > 1 && !HANDLED.includes(row.status)).forEach(row => add("back|" + row.seasonsBack,
    `Last edition ${row.seasonsBack} seasons ago (${seasonLabel(seasonOf(row.source.start)).toLowerCase()})`, row, { whole: true }))
  return groups
}

// The header's counts (ticket 17): what Save creates, the sources handled already, the cancelled ones, the older seasons, the drafts with
// warnings or a name to check, and the gaps.
export const reviewCounts = ({ rows, gaps }) => {
  const ticked = rows.filter(row => row.ticked)
  return [
    plural(ticked.length, "draft") + " to save",
    `${rows.filter(row => row.status === ALREADY_PLANNED).length} already in the calendar`,
    `${rows.filter(row => row.status === "cancelled").length} cancelled`,
    `${rows.filter(row => HANDLED.includes(row.status)).length} declined or withdrawn`,
    `${rows.filter(row => row.seasonsBack > 1).length} from older seasons`,
    `${ticked.filter(row => row.warnings.length).length} with warnings`,
    `${ticked.filter(row => row.draft.checkName).length} to check the name`,
    plural(gaps.length, "gap"),
  ]
}

// What already handles a source (ticket 17): "Already in the calendar: #39022 Cupa Iernii 2026 (Sat 12 – Sun 13 Dec)", "Declined: P-1012
// Cupa Lunii (Sat 9 – Sun 10 Jan) on 11 Oct — “nu am terenuri”", "Withdrawn: P-1013 …".
const handledName = ref => `${named(ref)}${ref.days ? ` (${daysLabel(ref.days)})` : ""}`
export const handledLine = (row, timeZone) => {
  const by = row.handledBy
  if (!by) return null
  if (row.status === "refused") {
    const { note, at } = by.answer || {}
    return `Declined: ${handledName(by)}${at ? ` on ${shortDateLabel(at, timeZone)}` : ""}${note ? ` — “${note}”` : ""}`
  }
  if (row.status === "withdrawn") return `Withdrawn: ${handledName(by)}`
  return `Already in the calendar${by.tier === "ambiguous" ? " (ambiguous)" : ""}: ${handledName(by)}`
}
// The other tournaments an ambiguous pairing could be (ticket 09): "Other candidates: #38877 Galaxy Cup (Fri 12 – Sun 14 Jun)".
export const alternativesLine = row => {
  const others = [...(row.handledBy?.alternatives?.next || []), ...(row.handledBy?.alternatives?.previous || [])]
  return row.handledBy?.tier === "ambiguous" && others.length ? `Other candidates: ${others.map(handledName).join(", ")}` : null
}
// A source generated anyway while proposals continue it (D3 `holders`): saving makes another proposal of the same edition.
export const heldLine = row => (row.ticked && row.holders?.length
  ? `Generated anyway: ${plural(row.holders.length, "proposal")} already ${row.holders.length === 1 ? "continues" : "continue"} this edition` : null)

// After a save: "289 drafts saved: P-2101 – P-2389.", or one: "1 draft saved: P-2101.".
const numberValue = number => Number(String(number).replace(/\D/g, ""))
export const savedLine = proposals => {
  const numbers = proposals.map(({ number }) => number).sort((a, b) => numberValue(a) - numberValue(b))
  return `${plural(numbers.length, "draft")} saved: ${numbers.length > 1 ? `${numbers[0]} – ${numbers[numbers.length - 1]}` : numbers[0]}.`
}
// A batch refused because sources were planned meanwhile (D3): "2 rows were planned meanwhile (P-1050, P-1051): they are unticked now. Check them
// and save again."
export const plannedMeanwhileLine = rows => {
  const numbers = [...new Set(rows.flatMap(({ proposals }) => (proposals || []).map(({ number }) => number)))]
  const one = rows.length === 1
  return `${plural(rows.length, "row")} ${one ? "was" : "were"} planned meanwhile${numbers.length ? ` (${numbers.join(", ")})` : ""}: `
    + `${one ? "it is" : "they are"} unticked now. Check ${one ? "it" : "them"} and save again.`
}
